<script setup lang="ts">
interface ErrorProps {
  statusCode: number
  fatal: boolean
  unhandled: boolean
  statusMessage?: string
  data?: unknown
  cause?: unknown
}

const props = defineProps<{
  error: ErrorProps
}>()

const isDevelopment = process.env.NODE_ENV === 'development'

// Computed properties for error display
const errorTitle = computed(() => {
  if (props.error.statusCode === 404) {
    return 'Pagina niet gevonden'
  }
  return props.error.statusMessage || 'Er is een fout opgetreden'
})

const errorMessage = computed(() => {
  if (props.error.statusCode === 404) {
    return 'De pagina die u zoekt bestaat niet of is verplaatst.'
  }
  return 'Er is een onverwachte fout opgetreden. Probeer het later opnieuw.'
})

// Error handling methods
const handleError = () => {
  if (props.error.statusCode === 404) {
    // Navigate to home page for 404 errors
    navigateTo('/')
  } else {
    // Clear the error for other types
    clearError()
  }
}

const goBack = () => {
  // Go back in browser history
  if (useNuxtApp().$client) {
    window.history.back()
  }
}
</script>

<template>
  <div class="flex min-h-screen items-center justify-center bg-gray-50">
    <UCard class="w-full max-w-md text-center">
      <!-- Error Icon -->
      <div
        class="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100"
      >
        <UIcon
          name="i-heroicons-exclamation-triangle"
          class="h-8 w-8 text-red-600"
        />
      </div>

      <!-- Error Title -->
      <h1 class="mb-2 text-2xl font-bold text-gray-900">
        {{ errorTitle }}
      </h1>

      <!-- Error Message -->
      <p class="mb-6 text-gray-600">
        {{ errorMessage }}
      </p>

      <!-- Status Code (if available) -->
      <div v-if="error.statusCode" class="mb-6">
        <UBadge
          :label="error.statusCode.toString()"
          color="neutral"
          variant="soft"
        />
      </div>

      <!-- Action Buttons -->
      <div class="space-y-3">
        <UButton @click="handleError" color="primary" variant="solid" block>
          {{ error.statusCode === 404 ? 'Terug naar home' : 'Probeer opnieuw' }}
        </UButton>

        <UButton @click="goBack" color="neutral" variant="soft" block>
          Ga terug
        </UButton>
      </div>

      <!-- Additional Info for Developers -->
      <div v-if="isDevelopment" class="mt-8">
        <UCard class="text-left">
          <template #header>
            <h3 class="text-sm font-medium text-gray-700">Debug Info:</h3>
          </template>
          <pre class="overflow-auto text-xs text-gray-600">{{
            JSON.stringify(error, null, 2)
          }}</pre>
        </UCard>
      </div>
    </UCard>
  </div>
</template>
